<template>
  <div class="mobile-clue-select">
    <div v-if="disabled" class="disabled-box"></div>
    <div class="mobile-clue-select__trigger-wrapper" @click="handleOpen">
      <slot name="trigger" :value="modelValue">
        <div class="mobile-clue-select__trigger">
          <div v-if="modelValue?.id" class="mobile-clue-select__name text-overflow">
            {{ modelValue.name }}
          </div>
          <div v-else class="mobile-clue-select__placeholder">请选择线索</div>
          <i class="icon-arrow-down-s-line t-iconfont" />
        </div>
      </slot>
    </div>
    <el-drawer
      v-model="drawer"
      direction="btt"
      :show-close="false"
      size="80%"
      :with-header="false"
      @closed="handleClosed"
    >
      <div class="drawer-content">
        <div class="head">
          <div class="left" @click="drawer = false">
            <i class="icon-close-line t-iconfont" />
          </div>
          <div class="center">线索</div>
          <div class="right"></div>
        </div>
        <div class="content">
          <el-input
            v-model="searchValue"
            class="mobile-clue-select__search"
            clearable
            placeholder="搜索线索"
            :prefix-icon="Search"
            @input="handleSearch"
          />
          <div v-if="loading" class="mobile-clue-select__status">线索加载中</div>
          <div
            v-else-if="loadError"
            class="mobile-clue-select__status mobile-clue-select__status--action"
            role="button"
            @click="resetAndLoad"
          >
            加载失败，点击重试
          </div>
          <div v-else-if="clueList.length === 0" class="mobile-clue-select__status">
            暂无匹配结果
          </div>
          <div v-else class="mobile-clue-select__content">
            <van-list
              v-model:error="loadMoreError"
              v-model:loading="loadingMore"
              error-text="加载失败，点击重试"
              :finished="!hasMore"
              finished-text="已加载全部"
              :offset="150"
              @load="loadMore"
            >
              <template v-for="item in clueList" :key="item.id">
                <div class="mobile-clue-select__item" @click="handleSelectClue(item)">
                  <span class="text-overflow">{{ item.name }}</span>
                </div>
                <div class="bottom-line"></div>
              </template>
            </van-list>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { Search } from '@element-plus/icons-vue'
import { getClueList } from '@/api'

const PAGE_SIZE = 20
const SEARCH_DELAY = 300

const modelValue = defineModel('modelValue', { type: Object, required: true })
const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['change'])

const drawer = ref(false)
const searchValue = ref('')
const clueList = ref([])
const page = ref(1)
const rowsCount = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const loadError = ref(false)
const loadMoreError = ref(false)

let searchTimer = null
let queryVersion = 0

const hasMore = computed(() => clueList.value.length < rowsCount.value)

const fetchPage = async (pageNumber, version) => {
  try {
    const { result } = await getClueList({
      page: pageNumber,
      pageSize: PAGE_SIZE,
      nameLike: searchValue.value.trim()
    })
    if (version !== queryVersion) return

    const nextList = result?.clueList || []
    clueList.value = pageNumber === 1 ? nextList : [...clueList.value, ...nextList]
    page.value = pageNumber
    rowsCount.value = result?.pageHelper?.rowsCount || 0
  } catch (error) {
    if (version === queryVersion) {
      console.error('获取线索列表失败:', error)
      if (pageNumber === 1) loadError.value = true
      else loadMoreError.value = true
    }
  } finally {
    if (version === queryVersion) {
      loading.value = false
      loadingMore.value = false
    }
  }
}

const resetAndLoad = () => {
  const version = ++queryVersion
  page.value = 1
  rowsCount.value = 0
  clueList.value = []
  loadError.value = false
  loadMoreError.value = false
  loading.value = true
  loadingMore.value = false
  fetchPage(1, version)
}

const handleOpen = () => {
  if (props.disabled) return
  drawer.value = true
  resetAndLoad()
}

const handleSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(resetAndLoad, SEARCH_DELAY)
}

const loadMore = () => {
  if (loading.value || !hasMore.value) {
    loadingMore.value = false
    return
  }
  loadMoreError.value = false
  fetchPage(page.value + 1, queryVersion)
}

const handleSelectClue = (item) => {
  modelValue.value = { id: item.id, name: item.name }
  drawer.value = false
  emit('change', modelValue.value)
}

const handleClosed = () => {
  clearTimeout(searchTimer)
  queryVersion++
  searchValue.value = ''
  loading.value = false
  loadingMore.value = false
}

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
  queryVersion++
})
</script>

<style scoped lang="less">
.mobile-clue-select {
  flex: 1;
  position: relative;
  width: 100%;
  overflow: hidden;

  .disabled-box {
    position: absolute;
    inset: 0;
    background: rgba(243, 246, 249, 0.6);
    z-index: 10;
    cursor: not-allowed;
  }

  :deep(.el-drawer) {
    border-radius: 20px 20px 0 0;
  }
}

.mobile-clue-select__trigger {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  box-sizing: border-box;
}

.mobile-clue-select__trigger-wrapper {
  width: 100%;
}

.mobile-clue-select__name {
  flex: 1;
  width: 100%;
  margin-right: 8px;
  line-height: 18px;
}

.mobile-clue-select__placeholder {
  flex: 1;
  color: #c8c9cc;
  line-height: 18px;
}
.icon-arrow-down-s-line {
  line-height: 18px;
}

.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0 0 auto;
    padding: 13px 16px;
    color: #323233;
  }

  .left,
  .right {
    flex: 0 0 52px;
  }

  .left i {
    color: #969799;
    font-size: 24px;
    line-height: 24px;
  }

  .center {
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    text-align: center;
  }

  .content {
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
  }
}

.mobile-clue-select__search {
  padding: 12px;
  border-bottom: 1px solid #f2f3f5;

  :deep(.el-input__wrapper) {
    padding: 3px 8px;
    background: #f7f8fa;
    border-radius: 8px;
    box-shadow: none;
  }
}

.mobile-clue-select__content {
  flex: 1;
  overflow-y: auto;
}

.mobile-clue-select__item {
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 28px;
  color: #323233;
  line-height: 48px;
  box-sizing: border-box;
}

.mobile-clue-select__status {
  padding: 24px 16px;
  color: #969799;
  font-size: 14px;
  line-height: 20px;
  text-align: center;

  &--action {
    cursor: pointer;
  }
}

.bottom-line {
  height: 1px;
  margin-left: 28px;
  background: #e5e6eb;
}

.text-overflow {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
